import React from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { formatDate } from "@/utils/format";
import { ClientItem, CountryItem, ProvinceItem } from "@/pages/Clients/types";
import { ClientType } from "@/pages/Tenders/types";

export interface ClientDetailModalProps {
    client: ClientItem | null;
    isOpen: boolean;
    onClose: () => void;
    onEdit: (client: ClientItem) => void;
    onDelete: (client: ClientItem) => void;
    clientTypes?: ClientType[];
    countries?: CountryItem[];
    provinces?: ProvinceItem[];
}

export const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
    client,
    isOpen,
    onClose,
    onEdit,
    onDelete,
    clientTypes = [],
    countries = [],
    provinces = [],
}) => {
    const { t } = useTranslation("global");

    if (!client) {
        return null;
    }

    const clientType =
        client.client_type ||
        clientTypes.find((ct: ClientType) => ct.id === client.client_type_id);
    const clientTypeName = clientType?.name;

    const isPublic = clientTypeName
        ? clientTypeName.toLowerCase().includes("públic") ||
          clientTypeName.toLowerCase().includes("public")
        : false;
    const isCivil = clientTypeName
        ? clientTypeName.toLowerCase().includes("civil")
        : false;
    const defaultFallback = isPublic
        ? "success"
        : isCivil
          ? "neutral"
          : "warning";
    const defaultIcon = isPublic ? "landmark" : isCivil ? "hardHat" : "house";

    const typeColor = clientType?.color || defaultFallback;
    const typeIcon = clientType?.icon || defaultIcon;

    const countryName =
        client.country?.name ||
        (client.country_id
            ? countries.find((c: CountryItem) => c.id === client.country_id)
                  ?.name
            : null);
    const provinceName =
        client.province?.name ||
        (client.province_id
            ? provinces.find((p: ProvinceItem) => p.id === client.province_id)
                  ?.name
            : null);

    const handleEditClick = (): void => {
        onClose();
        onEdit(client);
    };

    const handleDeleteClick = (): void => {
        onClose();
        onDelete(client);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("clients.detail_title")}
            maxWidth="2xl"
            footer={
                <div className="flex w-full justify-center gap-2">
                    <Button
                        variant="danger"
                        className="w-32"
                        onClick={handleDeleteClick}
                        startIcon="trash"
                        text={t("common.delete")}
                    />
                    <Button
                        variant="warning"
                        className="w-32"
                        onClick={handleEditClick}
                        startIcon="edit"
                        text={t("common.edit")}
                    />
                    <Button
                        variant="secondary"
                        className="w-32"
                        icon="x"
                        onClick={onClose}
                        text={t("common.close")}
                    />
                </div>
            }
        >
            <div className="space-y-5">
                {/* Header card with name, status and type */}
                <div className="p-4 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-slate-50/70 dark:bg-white/[0.03] space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-11 h-11 rounded-xl bg-primary/10 dark:bg-primary/20 text-primary flex items-center justify-center shrink-0">
                                <Icon value="building" size={22} />
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-base font-bold text-slate-800 dark:text-zinc-100 truncate">
                                    {client.name}
                                </h3>
                                {client.tax_id && (
                                    <p className="text-xs font-mono text-slate-500 dark:text-zinc-400 mt-0.5">
                                        {t("clients.tax_id")}:{" "}
                                        <span className="font-semibold text-slate-700 dark:text-zinc-200">
                                            {client.tax_id}
                                        </span>
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge
                                color={client.is_active ? "success" : "neutral"}
                                value={
                                    client.is_active
                                        ? t("clients.status_active")
                                        : t("clients.status_inactive")
                                }
                            />
                            {clientTypeName && (
                                <Badge
                                    color={typeColor as any}
                                    icon={typeIcon}
                                    value={clientTypeName}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {/* Contact information card */}
                <div className="p-4 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-slate-50/40 dark:bg-white/[0.02] space-y-3">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60 dark:border-white/5">
                        <Icon
                            value="phone"
                            size={16}
                            className="text-primary"
                        />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-zinc-300">
                            {t("clients.contact_info")}
                        </h4>
                    </div>

                    <div className="space-y-2.5 text-sm">
                        <div className="flex items-center justify-between gap-2 py-1">
                            <span className="text-xs text-slate-500 dark:text-zinc-400">
                                {t("clients.phone")}
                            </span>
                            {client.phone ? (
                                <a
                                    href={`tel:${client.phone}`}
                                    className="font-medium text-slate-800 dark:text-zinc-200 hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-1.5"
                                >
                                    <Icon
                                        value="phone"
                                        size={13}
                                        className="text-slate-400"
                                    />
                                    <span>{client.phone}</span>
                                </a>
                            ) : (
                                <span className="text-xs text-slate-400">
                                    —
                                </span>
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-2 py-1">
                            <span className="text-xs text-slate-500 dark:text-zinc-400">
                                {t("clients.email")}
                            </span>
                            {client.email ? (
                                <a
                                    href={`mailto:${client.email}`}
                                    className="font-medium text-slate-800 dark:text-zinc-200 hover:text-primary dark:hover:text-primary transition-colors flex items-center gap-1.5"
                                >
                                    <Icon
                                        value="mail"
                                        size={13}
                                        className="text-slate-400"
                                    />
                                    <span>{client.email}</span>
                                </a>
                            ) : (
                                <span className="text-xs text-slate-400">
                                    —
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Location information card */}
                <div className="p-4 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-slate-50/40 dark:bg-white/[0.02] space-y-3">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60 dark:border-white/5">
                        <Icon
                            value="globe"
                            size={16}
                            className="text-primary"
                        />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-zinc-300">
                            {t("clients.location_info")}
                        </h4>
                    </div>

                    <div className="space-y-2.5 text-sm">
                        <div className="flex items-start justify-between gap-2 py-1">
                            <span className="text-xs text-slate-500 dark:text-zinc-400 shrink-0">
                                {t("clients.address")}
                            </span>
                            <span className="font-medium text-slate-800 dark:text-zinc-200 text-right">
                                {client.address || ""}
                            </span>
                        </div>

                        <div className="flex items-center justify-between gap-2 py-1">
                            <span className="text-xs text-slate-500 dark:text-zinc-400">
                                {t("clients.postal_code")}
                            </span>
                            <span className="font-medium text-slate-800 dark:text-zinc-200">
                                {client.postal_code || ""}
                            </span>
                        </div>

                        <div className="flex items-center justify-between gap-2 py-1">
                            <span className="text-xs text-slate-500 dark:text-zinc-400">
                                {t("clients.city")}
                            </span>
                            <span className="font-medium text-slate-800 dark:text-zinc-200">
                                {client.city || ""}
                            </span>
                        </div>

                        <div className="flex items-center justify-between gap-2 py-1">
                            <span className="text-xs text-slate-500 dark:text-zinc-400">
                                {t("clients.province")}
                            </span>
                            <span className="font-medium text-slate-800 dark:text-zinc-200">
                                {provinceName || ""}
                            </span>
                        </div>

                        <div className="flex items-center justify-between gap-2 py-1">
                            <span className="text-xs text-slate-500 dark:text-zinc-400">
                                {t("clients.country")}
                            </span>
                            <span className="font-medium text-slate-800 dark:text-zinc-200">
                                {countryName || ""}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Notes card */}
                <div className="p-4 rounded-2xl border border-slate-200/80 dark:border-white/10 bg-slate-50/40 dark:bg-white/[0.02] space-y-2">
                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60 dark:border-white/5">
                        <Icon
                            value="fileText"
                            size={16}
                            className="text-primary"
                        />
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-zinc-300">
                            {t("clients.notes")}
                        </h4>
                    </div>

                    {client.notes ? (
                        <div className="text-xs text-slate-700 dark:text-zinc-300 whitespace-pre-wrap leading-relaxed p-3 rounded-xl bg-white dark:bg-white/5 border border-slate-200/60 dark:border-white/5">
                            {client.notes}
                        </div>
                    ) : (
                        <p className="text-xs text-slate-400 italic py-1">
                            {t("clients.no_notes")}
                        </p>
                    )}
                </div>

                {/* Metadata card (dates) */}
                {(client.created_at || client.updated_at) && (
                    <div className="flex items-center justify-between text-[11px] text-slate-400 px-1 pt-1">
                        {client.created_at ? (
                            <span>
                                {t("clients.created_at")}:{" "}
                                {formatDate(client.created_at, true)}
                            </span>
                        ) : (
                            <span />
                        )}
                        {client.updated_at ? (
                            <span>
                                {t("clients.updated_at")}:{" "}
                                {formatDate(client.updated_at, true)}
                            </span>
                        ) : null}
                    </div>
                )}
            </div>
        </Modal>
    );
};
